import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import FAQAccordion from '../components/FAQAccordion';
import { FAQPageSchema, BreadcrumbSchema, posFaqItems } from '../components/JsonLdSchema';

const posFeatures = [
  { icon: '🖨️', title: 'Cost Center-wise KOT Printing', desc: 'Assign a unique printer to multiple cost centers and send KOTs to respective cost centers. Sync every KOT with the master POS to track all orders.' },
  { icon: '🪑', title: 'Table & Area Management',       desc: 'Configure different dine-in areas with their menus, pricing, tax rates and customizable seating. Track vacancies, steward assignments and table-wise orders.' },
  { icon: '🖥️', title: 'Multi Billing Terminals',       desc: 'Create multiple billing counters synced with one order terminal so captains can generate KOTs and cashiers settle bills from any counter.' },
  { icon: '🧾', title: 'Customizable Bill Format',      desc: 'Print your restaurant logo, bill break-ups, customer details or a dynamic QR code for payments right at the billing counter.' },
  { icon: '💰', title: 'Configure Taxes & Discounts',   desc: 'Easily configure and levy different taxes, update tax rates and offer discounts depending on your service types, regional norms and business needs.' },
  { icon: '⚡', title: 'Uninterrupted Billing',         desc: 'Experience smooth checkout with tablet, mobile, and desktop-based Touch POS systems that ensure smooth checkout, even offline.' },
];

export default function Pos() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="POS Billing Software for Cafés & QSRs | DPOS"
        description="DPOS POS billing software for cafes and QSRs: KOT printing, table management, multi-terminal billing, and uninterrupted billing, even offline."
        canonical="/pos"
      />
      <FAQPageSchema items={posFaqItems} />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'POS & Billing Software' },
      ]} />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #fff 0%, #fef2f2 50%, #fff 100%)', padding: '72px 24px 64px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }} className="pos-hero-grid">
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em' }}>DPOS</span>
            <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, color: '#111827', lineHeight: 1.15, margin: '12px 0 20px' }}>
              POS Billing, KOT & Table Management
            </h1>
            <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.8, marginBottom: 16 }}>
              A complete cloud-based POS and billing solution designed for restaurants, cafés, QSRs, cloud kitchens, and retail businesses of all sizes.
            </p>
            <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.8, marginBottom: 32 }}>
              Fast billing, GST invoicing, KOT management, table tracking, multi-terminal support — all in one system that works on any device, even offline.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/contact" style={{ background: '#CC0000', color: '#fff', padding: '12px 28px', borderRadius: 8, fontWeight: 600, fontSize: 15, display: 'inline-block' }}>Contact for Free Demo →</Link>
              <Link to="/contact" style={{ background: 'transparent', color: '#CC0000', padding: '12px 28px', borderRadius: 8, fontWeight: 600, fontSize: 15, display: 'inline-block', border: '1.5px solid #CC0000' }}>Contact Sales</Link>
            </div>
          </div>
          <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>
            <img src="/pos-touch.webp" alt="DPOS POS Terminal" fetchpriority="high" style={{ width: '100%', height: 400, objectFit: 'cover', display: 'block' }} />
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Features</p>
          <h2 style={{ fontSize: 'clamp(26px, 3vw, 40px)', fontWeight: 800, color: '#111827', marginBottom: 12 }}>Everything You Need at the Counter</h2>
          <p style={{ fontSize: 16, color: '#6b7280', marginBottom: 56, maxWidth: 520 }}>
            Purpose-built for the F&B industry — from a single outlet to a multi-location chain.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
            {posFeatures.map(f => (
              <div key={f.title} style={{ background: '#f9fafb', borderRadius: 16, padding: '28px 24px', border: '1px solid #e5e7eb', transition: 'all 0.2s', cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#CC0000'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(204,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: 48, height: 48, background: '#fef2f2', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 16 }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 24px', background: '#111827', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
            Ready to upgrade your billing?
          </h2>
          <p style={{ fontSize: 16, color: '#9ca3af', marginBottom: 36, lineHeight: 1.7 }}>
            Get a free demo and see how DPOS fits your restaurant.
          </p>
          <Link to="/contact" style={{ background: '#CC0000', color: '#fff', padding: '14px 36px', borderRadius: 8, fontWeight: 600, fontSize: 16, display: 'inline-block' }}>Contact for Free Demo</Link>
        </div>
      </section>

      <section style={{ padding: '56px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 20 }}>Guides on POS and Billing</h2>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none', padding: 0 }}>
            <li><Link to="/blog/restaurant-gst-billing-software-india" style={{ color: '#CC0000', fontWeight: 600 }}>Restaurant GST billing software in India: what to look for</Link></li>
            <li><Link to="/blog/kot-printing-for-restaurants" style={{ color: '#CC0000', fontWeight: 600 }}>KOT printing for restaurants: how it works and how to set it up</Link></li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '80px 24px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'center', marginBottom: 6 }}>
            Frequently Asked Questions
          </p>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', textAlign: 'center', marginBottom: 40 }}>
            POS & Billing, Answered
          </h2>
          <FAQAccordion items={posFaqItems} />
        </div>
      </section>

      <style>{`@media(max-width:768px){.pos-hero-grid{grid-template-columns:1fr !important;}}`}</style>
    </main>
  );
}
